import { useState, useCallback, useRef, useEffect } from 'react';
// ✅ FIXED IMPORT PATH - should be from lib directory
import { 
  convertToVideo, 
  preloadFFmpeg, 
  estimateConversionTime,
  checkBrowserSupport 
} from '@/lib/convertToVideo';

export interface ConversionState {
  isConverting: boolean;
  progress: number;
  error: string | null;
  estimatedTime: string | null;
  conversionSpeed: number | null; // MB/s
  browserSupported: boolean;
  performanceWarnings: string[];
}

export function useVideoConversion() {
  const [state, setState] = useState<ConversionState>({
    isConverting: false,
    progress: 0,
    error: null,
    estimatedTime: null,
    conversionSpeed: null,
    browserSupported: true,
    performanceWarnings: [],
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const startTimeRef = useRef<number>(0);
  const fileSizeRef = useRef<number>(0);
  const processedBytesRef = useRef<number>(0);

  // ✅ Enhanced initialization with browser support check
  useEffect(() => {
    const { supported, issues } = checkBrowserSupport();
    
    setState(prev => ({
      ...prev,
      browserSupported: supported,
      performanceWarnings: issues,
    }));

    // Preload FFmpeg only if browser is supported
    if (supported) {
      preloadFFmpeg().catch(error => {
        console.warn('FFmpeg preload failed:', error);
        setState(prev => ({
          ...prev,
          performanceWarnings: [...prev.performanceWarnings, 'FFmpeg preload failed - first conversion may be slower'],
        }));
      });
    }

    // Do not terminate the shared FFmpeg worker on component unmount.
    // React StrictMode intentionally mounts and unmounts effects twice in development.
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ✅ Enhanced conversion with better performance tracking
  const convert = useCallback(async (
    audio: string | File,
    image: string | File
  ): Promise<Blob | null> => {
    // Check browser support before starting
    if (!state.browserSupported) {
      setState(prev => ({
        ...prev,
        error: 'Your browser does not support video conversion. Please use a modern browser like Chrome, Firefox, or Safari.',
      }));
      return null;
    }

    // Reset state
    setState(prev => ({
      ...prev,
      isConverting: true,
      progress: 0,
      error: null,
      estimatedTime: audio instanceof File ? estimateConversionTime(audio) : null,
      conversionSpeed: null,
    }));

    // Setup conversion tracking
    abortControllerRef.current = new AbortController();
    startTimeRef.current = performance.now();
    fileSizeRef.current = audio instanceof File ? audio.size : 0;
    processedBytesRef.current = 0;

    try {
      const result = await convertToVideo(
        audio,
        image,
        (progress) => {
          // Calculate real-time conversion speed
          const elapsed = (performance.now() - startTimeRef.current) / 1000;
          const currentProcessedBytes = (fileSizeRef.current * progress) / 100;
          processedBytesRef.current = currentProcessedBytes;
          
          // Calculate speed (MB/s)
          const speed = elapsed > 1 ? currentProcessedBytes / elapsed / 1024 / 1024 : 0;
          
          // Update remaining time estimate
          let estimatedTimeRemaining = null;
          if (speed > 0 && progress > 5 && progress < 95) {
            const remainingBytes = fileSizeRef.current - currentProcessedBytes;
            const remainingSeconds = remainingBytes / (speed * 1024 * 1024);
            estimatedTimeRemaining = remainingSeconds > 60 
              ? `${Math.round(remainingSeconds / 60)}m ${Math.round(remainingSeconds % 60)}s`
              : `${Math.round(remainingSeconds)}s`;
          }

          setState(prev => ({
            ...prev,
            progress: Math.min(progress, 100),
            conversionSpeed: speed > 0.1 ? Math.round(speed * 100) / 100 : null,
            estimatedTime: estimatedTimeRemaining || prev.estimatedTime,
          }));
        }
      );

      // Success state
      const totalTime = (performance.now() - startTimeRef.current) / 1000;
      const avgSpeed = fileSizeRef.current / totalTime / 1024 / 1024;
      
      setState(prev => ({
        ...prev,
        isConverting: false,
        progress: 100,
        conversionSpeed: Math.round(avgSpeed * 100) / 100,
        estimatedTime: null,
      }));

      return result;

    } catch (error: any) {
      // Check if error was due to abort
      if (abortControllerRef.current?.signal.aborted) {
        setState(prev => ({
          ...prev,
          isConverting: false,
          error: 'Conversion was cancelled',
        }));
      } else {
        setState(prev => ({
          ...prev,
          isConverting: false,
          error: error.message || 'Conversion failed unexpectedly',
        }));
      }
      return null;
    } finally {
      abortControllerRef.current = null;
    }
  }, [state.browserSupported]);

  // ✅ Enhanced cancellation
  const cancel = useCallback(() => {
    if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
      abortControllerRef.current.abort();
      setState(prev => ({
        ...prev,
        isConverting: false,
        progress: 0,
        error: 'Conversion cancelled by user',
        conversionSpeed: null,
        estimatedTime: null,
      }));
    }
  }, []);

  // ✅ Enhanced reset with memory cleanup
  const reset = useCallback(() => {
    // Cancel any ongoing conversion
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setState(prev => ({
      ...prev,
      isConverting: false,
      progress: 0,
      error: null,
      estimatedTime: null,
      conversionSpeed: null,
    }));

    // Reset tracking refs
    startTimeRef.current = 0;
    fileSizeRef.current = 0;
    processedBytesRef.current = 0;
  }, []);

  // ✅ Utility to check if conversion is possible
  const canConvert = useCallback((audioFile?: File) => {
    if (!state.browserSupported) return false;
    if (!audioFile) return true;
    
    // Check file size limits
    if (audioFile.size > 150 * 1024 * 1024) return false;
    
    return true;
  }, [state.browserSupported]);

  // ✅ Get performance recommendations
  const getPerformanceRecommendations = useCallback(() => {
    const recommendations: string[] = [];
    
    if (state.performanceWarnings.length > 0) {
      recommendations.push(...state.performanceWarnings);
    }
    
    if (!state.browserSupported) {
      recommendations.push('Switch to a modern browser for video conversion support');
    }
    
    // Memory-based recommendations
    if (typeof performance !== 'undefined' && 'memory' in performance) {
      const memory = (performance as any).memory;
      if (memory?.usedJSHeapSize > memory?.jsHeapSizeLimit * 0.8) {
        recommendations.push('High memory usage detected - consider refreshing the page');
      }
    }
    
    return recommendations;
  }, [state.browserSupported, state.performanceWarnings]);

  return {
    // State
    ...state,
    
    // Actions
    convert,
    cancel,
    reset,
    
    // Utilities
    canConvert,
    getPerformanceRecommendations,
    
    // Computed values
    isIdle: !state.isConverting && !state.error,
    hasError: !!state.error,
    isComplete: state.progress === 100 && !state.isConverting,
  };
}